import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import api from '../api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  // Usuario REAL (el que inició sesión)
  const [realUser, setRealUser] = useState(() => {
    const saved = localStorage.getItem('lgr_user')
    return saved ? JSON.parse(saved) : null
  })
  // Rol que el Super Administrador está imitando (null = ninguno)
  const [impersonateRole, setImpersonateRole] = useState(() => localStorage.getItem('lgr_impersonate') || null)
  // Usuario puntual imitado ({id, nombre} — Martin, 3 sep 2026) y su ficha
  // fresca (la respuesta de /me imitando, con su rol y su lista reales)
  const [impersonateUser, setImpersonateUserState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lgr_impersonate_user')) } catch { return null }
  })
  const [impersonatedData, setImpersonatedData] = useState(null)

  // Guarda la sesión que devolvió /login o /login/2fa.
  const abrirSesion = useCallback((data) => {
    localStorage.setItem('lgr_token', data.token)
    localStorage.setItem('lgr_user', JSON.stringify(data.usuario))
    localStorage.removeItem('lgr_impersonate')
    localStorage.removeItem('lgr_impersonate_user')
    // "Confiar en este dispositivo por 30 días": el token vive en este
    // navegador y /login lo manda para saltear el código.
    if (data.dispositivo) localStorage.setItem('lgr_dispositivo', data.dispositivo)
    setImpersonateRole(null)
    setImpersonateUserState(null)
    setRealUser(data.usuario)
    return data
  }, [])

  // Primer paso. Con el segundo factor activado (y sin dispositivo confiable
  // vigente) el backend NO emite token: devuelve {requiere_2fa, desafio} y el
  // login sigue en login2fa().
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/login', { email, password })
    if (data.requiere_2fa) return { requiere_2fa: true, desafio: data.desafio }
    abrirSesion(data)
    return data.usuario
  }, [abrirSesion])

  // Segundo paso: código de la app (6 dígitos) o de rescate (XXXXX-XXXXX).
  const login2fa = useCallback(async (desafio, codigo, confiar) => {
    const { data } = await api.post('/login/2fa', { desafio, codigo, confiar: !!confiar })
    return abrirSesion(data)
  }, [abrirSesion])

  const logout = useCallback(async () => {
    try {
      await api.post('/logout')
    } finally {
      localStorage.removeItem('lgr_token')
      localStorage.removeItem('lgr_user')
      localStorage.removeItem('lgr_impersonate')
      localStorage.removeItem('lgr_impersonate_user')
      setImpersonateRole(null)
      setImpersonateUserState(null)
      setRealUser(null)
    }
  }, [])

  const isSuperAdmin = realUser?.rol === 'super_administrador'

  // Usuario "efectivo":
  //  - super-admin imitando un rol → se comporta como ese rol
  //  - super-admin sin imitar → se comporta como 'administrador' para permisos/UI
  //    (así pasa todos los chequeos de rol; su identidad real queda en realUser/isSuperAdmin)
  const user = useMemo(() => {
    if (impersonateUser && isSuperAdmin) {
      // La ficha fresca del imitado llega por /me; hasta entonces, un
      // esqueleto sin rol (los gates fallan cerrados un instante).
      return impersonatedData || { id: impersonateUser.id, nombre: impersonateUser.nombre, rol: null, roles: [] }
    }
    if (impersonateRole && isSuperAdmin) {
      return { ...realUser, rol: impersonateRole, roles: [impersonateRole] }
    }
    if (isSuperAdmin) {
      return { ...realUser, rol: 'administrador', roles: ['administrador'] }
    }
    return realUser
  }, [realUser, impersonateRole, impersonateUser, impersonatedData, isSuperAdmin])

  // Refresca el usuario real con la respuesta de /me (Layout la pide al
  // montar). Sin esto, un cambio de rol hecho por el admin no se veía hasta
  // que el usuario cerrara sesión y volviera a entrar: los gates client-side
  // (adminOnly de SISTEMA, hasAnyRole de los botones) seguían evaluando el
  // snapshot de localStorage del día del login (caso Alan, 11/08/2026).
  // OJO: imitando un rol NO se pisa — /me respondería con el rol imitado y
  // se perdería la identidad real del super admin.
  const actualizarUsuario = useCallback((usuario) => {
    // Imitando a un USUARIO, /me responde con la ficha del imitado: va a su
    // propio estado, jamás pisa la identidad real guardada.
    if (impersonateUser) {
      setImpersonatedData(usuario)
      return
    }
    if (impersonateRole) return
    localStorage.setItem('lgr_user', JSON.stringify(usuario))
    setRealUser(usuario)
  }, [impersonateRole, impersonateUser])

  // Cambia (o limpia) el rol imitado y recarga, para que TODO (menú + datos) se pida con él
  const setImpersonate = useCallback((role) => {
    if (role) localStorage.setItem('lgr_impersonate', role)
    else localStorage.removeItem('lgr_impersonate')
    localStorage.removeItem('lgr_impersonate_user')
    window.location.assign('/dashboard')
  }, [])

  // "Ver como" una PERSONA: {id, nombre} o null para volver. Recarga entera,
  // igual que la imitación de rol, para que menú y datos se pidan como ella.
  const setImpersonateUsuario = useCallback((u) => {
    if (u) localStorage.setItem('lgr_impersonate_user', JSON.stringify({ id: u.id, nombre: u.nombre }))
    else localStorage.removeItem('lgr_impersonate_user')
    localStorage.removeItem('lgr_impersonate')
    window.location.assign('/dashboard')
  }, [])

  const hasRole = useCallback((role) => {
    if (!user) return false
    const roles = user.roles || [user.rol]
    return roles.includes(role)
  }, [user])

  const hasAnyRole = useCallback((roles) => roles.some(hasRole), [hasRole])

  /**
   * ¿Es del equipo de VENTAS? Se pregunta por FAMILIA, no por nombre exacto.
   *
   * Los roles se renombran y se abren: "ventas" pasó a ser "ventas call" y
   * apareció "ventas sucursal" (ago 2026), y todo lo que preguntaba
   * `hasAnyRole([... 'ventas'])` empezó a dar false — los vendedores dejaron de
   * ver el botón de crear NV y las acciones del detalle, o sea no podían
   * trabajar. Con el prefijo, un rol de ventas nuevo funciona el día que se
   * crea, sin tocar seis archivos.
   */
  const esVentas = useCallback(() => {
    const roles = user?.roles || [user?.rol]
    return roles.some(r => /^ventas\b/i.test(String(r || '').trim()))
  }, [user])

  /** Los tres roles que operan una venta: admin, supervisor y cualquiera de ventas. */
  const rolOperativoVentas = useCallback(
    () => hasAnyRole(['administrador', 'supervisor']) || esVentas(),
    [hasAnyRole, esVentas],
  )

  return (
    <AuthContext.Provider value={{ user, realUser, isSuperAdmin, impersonateRole, impersonateUser, setImpersonate, setImpersonateUsuario, actualizarUsuario, login, login2fa, logout, hasRole, hasAnyRole, esVentas, rolOperativoVentas }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider')
  return ctx
}
